import React, { useState, useEffect, useMemo, useCallback } from 'react'
import WheelPicker from 'codepunter-react-wheelpicker'
import moment from 'moment'
import './TimeWheelPicker.css'

const TimeWheelPicker = ({
    value,
    onChange,
    minTime,
    maxTime,
    containerClassName,
    styles = {},
    intervalMinutes = 10, // Time interval in minutes (default: 10 minutes)
}) => {
    // Generate hour options (0-23)
    const hourOptions = useMemo(() => {
        const hours = []
        for (let i = 0; i < 24; i++) {
            hours.push(String(i).padStart(2, '0'))
        }
        return hours
    }, [])

    // Generate minute options based on interval
    const minuteOptions = useMemo(() => {
        const minutes = []
        for (let i = 0; i < 60; i += intervalMinutes) {
            minutes.push(String(i).padStart(2, '0'))
        }
        return minutes
    }, [intervalMinutes])

    // Parse minTime and maxTime
    const parsedMinTime = useMemo(() => {
        if (!minTime) return null
        if (minTime instanceof Date) return minTime
        if (typeof minTime === 'string') {
            // Try to parse as time string (HH:mm)
            const timeMatch = minTime.match(/^(\d{2}):(\d{2})$/)
            if (timeMatch) {
                const date = new Date()
                date.setHours(parseInt(timeMatch[1]), parseInt(timeMatch[2]), 0, 0)
                return date
            }
            // Try to parse as Date string
            return new Date(minTime)
        }
        return null
    }, [minTime])

    const parsedMaxTime = useMemo(() => {
        if (!maxTime) return null
        if (maxTime instanceof Date) return maxTime
        if (typeof maxTime === 'string') {
            // Try to parse as time string (HH:mm)
            const timeMatch = maxTime.match(/^(\d{2}):(\d{2})$/)
            if (timeMatch) {
                const date = new Date()
                date.setHours(parseInt(timeMatch[1]), parseInt(timeMatch[2]), 0, 0)
                return date
            }
            // Try to parse as Date string
            return new Date(maxTime)
        }
        return null
    }, [maxTime])

    // Get current time from value
    const getCurrentTime = () => {
        if (value instanceof Date) {
            return {
                hour: value.getHours(),
                minute: Math.floor(value.getMinutes() / intervalMinutes) * intervalMinutes,
            }
        }
        if (typeof value === 'string') {
            const timeMatch = value.match(/^(\d{2}):(\d{2})$/)
            if (timeMatch) {
                const hour = parseInt(timeMatch[1])
                const minute = Math.floor(parseInt(timeMatch[2]) / intervalMinutes) * intervalMinutes
                return { hour, minute }
            }
        }
        // Default to minTime or 0:00
        if (parsedMinTime) {
            return {
                hour: parsedMinTime.getHours(),
                minute: Math.floor(parsedMinTime.getMinutes() / intervalMinutes) * intervalMinutes,
            }
        }
        return { hour: 0, minute: 0 }
    }

    const [selectedHour, setSelectedHour] = useState(() => {
        const current = getCurrentTime()
        return current.hour
    })

    const [selectedMinute, setSelectedMinute] = useState(() => {
        const current = getCurrentTime()
        return current.minute
    })

    // Update state when value changes
    useEffect(() => {
        const current = getCurrentTime()
        setSelectedHour(current.hour)
        setSelectedMinute(current.minute)
    }, [value, intervalMinutes])

    // Filter available hours based on minTime and maxTime
    const availableHours = useMemo(() => {
        if (!parsedMinTime && !parsedMaxTime) return hourOptions

        const minHour = parsedMinTime ? parsedMinTime.getHours() : 0
        const maxHour = parsedMaxTime ? parsedMaxTime.getHours() : 23

        return hourOptions.filter((hour, index) => {
            const hourNum = parseInt(hour)
            return hourNum >= minHour && hourNum <= maxHour
        })
    }, [hourOptions, parsedMinTime, parsedMaxTime])

    // Filter available minutes based on selected hour and minTime/maxTime
    const availableMinutes = useMemo(() => {
        const minutes = minuteOptions

        // If minTime is set and selected hour equals min hour
        if (parsedMinTime && selectedHour === parsedMinTime.getHours()) {
            const minMinute = Math.ceil(parsedMinTime.getMinutes() / intervalMinutes) * intervalMinutes
            return minutes.filter(min => parseInt(min) >= minMinute)
        }

        // If maxTime is set and selected hour equals max hour
        if (parsedMaxTime && selectedHour === parsedMaxTime.getHours()) {
            const maxMinute = Math.floor(parsedMaxTime.getMinutes() / intervalMinutes) * intervalMinutes
            return minutes.filter(min => parseInt(min) <= maxMinute)
        }

        return minutes
    }, [minuteOptions, selectedHour, parsedMinTime, parsedMaxTime, intervalMinutes])

    // Notify parent component of time change
    const notifyTimeChange = useCallback(
        (hour, minute) => {
            if (onChange) {
                const timeString = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
                onChange(timeString)
            }
        },
        [onChange]
    )

    // Adjust minute when availableMinutes changes (e.g., when hour changes)
    useEffect(() => {
        const minuteStr = String(selectedMinute).padStart(2, '0')
        const isMinuteAvailable = availableMinutes.includes(minuteStr)

        if (!isMinuteAvailable && availableMinutes.length > 0) {
            // Find the closest available minute
            const currentMinute = selectedMinute
            const availableMinutesNum = availableMinutes.map(m => parseInt(m))
            const closestMinute = availableMinutesNum.reduce((prev, curr) => {
                return Math.abs(curr - currentMinute) < Math.abs(prev - currentMinute) ? curr : prev
            })
            setSelectedMinute(closestMinute)
            notifyTimeChange(selectedHour, closestMinute)
        }
    }, [availableMinutes, selectedHour, selectedMinute, notifyTimeChange])

    // Get default hour index
    const defaultHourIndex = useMemo(() => {
        const hourStr = String(selectedHour).padStart(2, '0')
        const index = availableHours.indexOf(hourStr)
        return index >= 0 ? index : 0
    }, [selectedHour, availableHours])

    // Get default minute index
    const defaultMinuteIndex = useMemo(() => {
        const minuteStr = String(selectedMinute).padStart(2, '0')
        const index = availableMinutes.indexOf(minuteStr)
        return index >= 0 ? index : 0
    }, [selectedMinute, availableMinutes])

    // Handle hour selection
    const handleHourChange = index => {
        const newHour = parseInt(availableHours[index])
        setSelectedHour(newHour)

        // Adjust minute if needed
        let newMinute = selectedMinute

        // If minTime is set and selected hour equals min hour
        if (parsedMinTime && newHour === parsedMinTime.getHours()) {
            const minMinute = Math.ceil(parsedMinTime.getMinutes() / intervalMinutes) * intervalMinutes
            if (newMinute < minMinute) {
                newMinute = minMinute
            }
        }

        // If maxTime is set and selected hour equals max hour
        if (parsedMaxTime && newHour === parsedMaxTime.getHours()) {
            const maxMinute = Math.floor(parsedMaxTime.getMinutes() / intervalMinutes) * intervalMinutes
            if (newMinute > maxMinute) {
                newMinute = maxMinute
            }
        }

        setSelectedMinute(newMinute)
        notifyTimeChange(newHour, newMinute)
    }

    // Handle minute selection
    const handleMinuteChange = index => {
        const newMinute = parseInt(availableMinutes[index])
        setSelectedMinute(newMinute)
        notifyTimeChange(selectedHour, newMinute)
    }

    // Default styles
    const defaultStyles = {
        height: 50,
        fontSize: 18,
        parentHeight: 200,
        selectedBackgroundColor: '#4eac6d',
        selectedTextColor: '#fff',
        unselectedTextColor: '#888',
        animation: 'flat',
        selectedItemStyles: {
            borderRadius: '8px',
            padding: '5px 10px',
        },
        disableTextHighlight: true,
        ...styles,
    }

    return (
        <div className={`time-wheel-picker ${containerClassName || ''}`}>
            <div className="time-wheel-picker-container">
                <div className="time-wheel-picker-hour">
                    <WheelPicker
                        data={availableHours}
                        height={defaultStyles.height}
                        fontSize={defaultStyles.fontSize}
                        parentHeight={defaultStyles.parentHeight}
                        defaultSelection={defaultHourIndex}
                        selectedBackgroundColor={defaultStyles.selectedBackgroundColor}
                        selectedTextColor={defaultStyles.selectedTextColor}
                        unselectedTextColor={defaultStyles.unselectedTextColor}
                        updateSelection={handleHourChange}
                        scrollerId="time-wheel-picker-hour"
                        animation={defaultStyles.animation}
                        selectedItemStyles={defaultStyles.selectedItemStyles}
                        disableTextHighlight={defaultStyles.disableTextHighlight}
                    />
                </div>
                <div className="time-wheel-picker-separator">:</div>
                <div className="time-wheel-picker-minute">
                    <WheelPicker
                        data={availableMinutes}
                        height={defaultStyles.height}
                        fontSize={defaultStyles.fontSize}
                        parentHeight={defaultStyles.parentHeight}
                        defaultSelection={defaultMinuteIndex}
                        selectedBackgroundColor={defaultStyles.selectedBackgroundColor}
                        selectedTextColor={defaultStyles.selectedTextColor}
                        unselectedTextColor={defaultStyles.unselectedTextColor}
                        updateSelection={handleMinuteChange}
                        scrollerId="time-wheel-picker-minute"
                        animation={defaultStyles.animation}
                        selectedItemStyles={defaultStyles.selectedItemStyles}
                        disableTextHighlight={defaultStyles.disableTextHighlight}
                    />
                </div>
            </div>
        </div>
    )
}

export default TimeWheelPicker
